import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    ElementRef,
    HostListener,
    Input,
    OnDestroy,
    ViewChild,
    inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { CurrencyConfig } from '@frontend/sports/common/client-config-data-access';
import { Store } from '@ngrx/store';

import { EpcotConfigService } from '../common/epcot-config.service';
import { UiManagerActions } from '../ui-manager/ui-manager.actions';
import { IUiRootState } from '../ui-manager/ui-manager.state';
import { UiStates } from '../ui-manager/ui-states.enum';
import { ISelectionProvider, NumpadOperation, NumpadState } from './model';
import { NumpadInputConnector } from './numpad-input-connector';
import { NumpadService } from './numpad.service';
import { INumpadRootState } from './store/numpad.state';

@Component({
    selector: 'ms-stake-field',
    templateUrl: './stake-field.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrls: ['./stake-field.component.scss'],
    providers: [NumpadInputConnector],
    standalone: false,
})
export class StakeFieldComponent implements AfterViewInit, OnDestroy, ISelectionProvider {
    @Input() disabled = false;
    @Input() emptyCharacter?: string;
    @Input() useStakeFieldCursor = false;
    @Input() adornmentStart?: string;
    @ViewChild('inputRef') inputRef?: ElementRef;
    selected = false;
    isEpcotEnabled: boolean;
    private readonly destroyRef = inject(DestroyRef);

    constructor(
        public numpadService: NumpadService,
        private currencyConfig: CurrencyConfig,
        private store: Store<INumpadRootState & IUiRootState>,
        private epcotConfigService: EpcotConfigService,
        private numpadInputConnector: NumpadInputConnector,
    ) {
        this.isEpcotEnabled = this.epcotConfigService.isEnabled();
    }

    @HostListener('click', ['$event'])
    stopPropagation(event: MouseEvent): void {
        event.stopPropagation();
        event.preventDefault();

        this.store.dispatch(UiManagerActions.removeState({ state: UiStates.MarketSubTypeTooltipShown }));
    }

    ngAfterViewInit(): void {
        if (this.useStakeFieldCursor && this.inputElement) {
            this.numpadInputConnector.init(this.inputElement);
            return;
        }

        this.numpadService.registerSelectionProvider(this);
        this.numpadService.state$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((state) => this.stateChange(state));
    }

    onClick(): void {
        if (this.useStakeFieldCursor) {
            this.numpadInputConnector.focus();
            return;
        }

        if (this.numpadService.currentState.isOpened) {
            this.toggleSelected();
        } else {
            this.numpadService.open();
        }
    }

    private stateChange(state: NumpadState): void {
        if (state.operation === NumpadOperation.Open) {
            this.setSelected();
            return;
        }

        this.setDeselected();
    }

    private toggleSelected(): void {
        if (this.selected) {
            this.setDeselected();

            return;
        }

        this.setSelected();
    }

    private setSelected(): void {
        if (this.numpadService.currentState.stake.isEmpty) {
            return;
        }

        this.selected = true;
    }

    private setDeselected(): void {
        this.selected = false;
    }

    getSelection(currentValue: string): { start: number; end: number } {
        return {
            start: this.selected ? 0 : currentValue.length,
            end: currentValue.length,
        };
    }

    getStake(state: NumpadState): string {
        return state.stake.isEmpty
            ? (this.emptyCharacter ?? '')
            : this.currencyConfig.showStakeCurrency
              ? state.stake.currencyFormatted
              : state.stake.formatted;
    }

    onBlur(): void {
        this.numpadInputConnector.clearFocus();
    }

    onFocus(): void {
        this.numpadInputConnector.focus();
    }

    ngOnDestroy(): void {
        this.numpadInputConnector.deInit();
    }

    private get inputElement(): HTMLInputElement {
        return <HTMLInputElement>this.inputRef?.nativeElement;
    }
}
